import TableOnly from "components/DataTable/TableOnly";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import PaginationWithState from "components/Pagination/PaginationWithState";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import CreateLabelModal from "./CreateLabelModal";
import ImportLabelModal from "./ImportLabelModal";

export default function ImportLabels({ selected, showModal }) {
  const dispatch = useDispatch();
  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [labels, setLabels] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const [gmailLabels, setGmailLabels] = useState(null);

  const [loading, setLoading] = useState(false);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  /*   All Functions
   ********************************************* */
  //get Data (uses POST /email-locations?page=&limit=)
  const getLabels = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
          ? query
          : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/email-locations?page=" + usedPage + "&limit=" + pageLimit,
          {
            grantEmail: selected.email,
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() }
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }
          setLabels(data?.data || []);
          setTotalPages(data?.totalPages || 0);
          /*   Get gmail labels
           **************** */
          // get labels Ids
          let labelIds = data?.data?.map((label) => label.labelId);
          const gmailLabels = await GetGmailLabels(selected.email);
          console.log(labelIds);
          console.log(gmailLabels.length);

          // mark imported labels
          const updatedGmailLabels = gmailLabels?.filter(
            (label) => !labelIds.includes(label.id)
          );

          console.log(updatedGmailLabels);

          setGmailLabels((prev) => updatedGmailLabels || []);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, searchTerm, pageLimit, filterBy.value, selected]
  );

  // Delete Template
  const deleteLabels = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete("/email-location/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        // refresh current page
        await getLabels();
        dispatch(
          setNotification({
            message: data?.message || "Deleted",
            type: "success",
          })
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to delete",
          type: "error",
        })
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Adds Button Function
  const handleAddLabels = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.post(
        "/email-location",
        { ...formData, grantEmail: selected.email },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        // after add refresh current page (or go to first page)
        setFormData({});
        setShowAddModal(false);
        // Refresh list
        setPageNumber(1);
        getLabels({ page: 1 });
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }

    setShowAddModal(false);
  };

  // Update Button Function
  const handleUpdateLabels = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/email-location",
        { ...formData, grantEmail: selected.email },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        setIsUpdateMode(false);
        setShowAddModal(false);
        // refresh list
        getLabels();
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Sorting Up
  const sortUp = async (id) => {
    try {
      const { data } = await API.post(
        "/email-location-sort-up",
        { emailLocationId: id },
        { headers: authHeader() }
      );
      if (data.success) {
        await getLabels();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Sorting Up
  const sortDown = async (id) => {
    try {
      const { data } = await API.post(
        "/email-location-sort-down",
        { emailLocationId: id },
        { headers: authHeader() }
      );
      if (data.success) {
        await getLabels();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdateData = async (item) => {
    setFormData(item);
    setIsUpdateMode(true);
    setShowAddModal(true);
  };

  const GetGmailLabels = async (grantEmail) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/emails/labels/list/by-grant-email",
        { grantEmail },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        return data.labels;
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to fetch Gmail labels",
            type: "error",
          })
        );
        setLoading(false);
        throw new Error(data?.message || "Failed to fetch Gmail labels");
      }
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getLabels(
        searchTerm && searchTerm !== ""
          ? { query: searchTerm, page: pageNumber }
          : { clearSearch: true, page: pageNumber }
      );
    }, 50);
    return () => clearTimeout(timeout);
  }, [pageNumber, pageLimit, filterBy]);

  /*   Table functions
   ********************************************* */
  let filters = [
    { value: "name", name: "Data Name" },
    { value: "contactName", name: "Contact Name" },
    { value: "contactNumber", name: "Contact Number" },
  ];

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy({ name: "Filter by All", value: null })}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "ID", value: "labelsId" },
    { name: "Label", value: "name" },
    { name: "Location", value: "locationName" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle py-1">{item.emailLocationId}</td>
      <td className="align-middle py-1">{item.labelName}</td>
      <td className="align-middle py-1">{item?.location?.locationName}</td>
      <td className="align-middle py-1 text-end table-action">
        <button
          onClick={() => {
            sortUp(item.emailLocationId);
          }}
          disabled={item.sortId === labels?.length} // assuming sort by sortId Desc
          className="btn btn-sm px-1 border-0"
        >
          <i className="mdi mdi-chevron-double-up p-0"></i>
        </button>
        <button
          onClick={() => {
            sortDown(item.emailLocationId);
          }}
          disabled={item.sortId === 1} // assuming sort by sortId Desc
          className="btn btn-sm px-1 border-0"
        >
          <i className="mdi mdi-chevron-double-down"></i>
        </button>
        <button
          disabled={loading}
          onClick={() => {
            handleUpdateData(item);
          }}
          className="btn btn-sm px-1 border-0"
        >
          <i className="mdi mdi-pencil"></i>
        </button>

        <DeleteModalButton
          btnClass={"btn btn-sm border-0 px-1"}
          deleteFunc={deleteLabels}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  return (
    <div>
      <h2 className="mb-3 ps-2">Team</h2>
      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex">
          {/* {buttons()} */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getLabels({ query: searchTerm, page: 1 });
              } else {
                getLabels({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getLabels({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          {/* Add button moved next to search for consistent layout */}
          <div className="">
            <button
              onClick={() => {
                setShowImportModal(true);
                setFormData({});
              }}
              type="button"
              className="btn btn-success ms-1"
            >
              Import Team
            </button>
            <button
              onClick={() => {
                setShowAddModal(true);
                setFormData({});
                setIsUpdateMode(false);
              }}
              type="button"
              className="btn btn-primary  ms-1"
            >
              Add Team
            </button>
          </div>
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 d-flex"
        style={{ minHeight: "50vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={labels}
            disableSorting={false}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      {/*   Add Modal
       ********************************************* */}
      {showAddModal && (
        <CreateLabelModal
          show={showAddModal}
          onClose={() => setShowAddModal(false)}
          labels={gmailLabels}
          refreshLabels={getLabels}
          isUpdateMode={isUpdateMode}
          formData={formData}
          setFormData={setFormData}
          submitFunction={isUpdateMode ? handleUpdateLabels : handleAddLabels}
        />
      )}

      {showImportModal && gmailLabels && (
        <ImportLabelModal
          show={showImportModal}
          onClose={() => setShowImportModal(false)}
          refreshLabels={getLabels}
          formData={formData}
          setFormData={setFormData}
          selectedEmail={selected.email}
          labels={gmailLabels}
        />
      )}
    </div>
  );
}
